import { Box, Stack } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useKeycloak } from "@react-keycloak/web";
import { useQueryClient } from "@tanstack/react-query";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import * as React from "react";
import { useState } from "react";
import Joyride from "react-joyride";
import {
  INSIGHT_TABLE_SWITCH_VALUES,
  INTERVIEW_NAVIGATION_TABS,
  INTERVIEW_PAGE_WIDTH_LIMITS,
} from "../../constants";
import useInterviewListVisibility from "../../hooks/useInterviewListVisibility";
import { discoveryUserguides } from "../../joyride";
import {
  baseAtom,
  getSelectedInterviewWithDefaultFallback,
  getViewMode,
  setDiscoveryFocusMode,
  setDiscoveryViewMode,
  setInsightSelection,
  setSelectedArtifactType,
} from "../../stores/jotai/base";
import { discoverySessionAtom } from "../../stores/jotai/discovery-session";
import { findingsAtom } from "../../stores/jotai/insights/findings";
import { interviewFindingsAtom } from "../../stores/jotai/insights/interviewFindings";
import { useGetCustomFindingTypes } from "../../tanstackQuery/queries/custom-finding-types";
import { useGetFindings } from "../../tanstackQuery/queries/findings";
import { useUpdateInterview } from "../../tanstackQuery/queries/interviews";
import { CustomSearch } from "../general/customSearch";
import DiscoveryNavigation from "./discovery-navigation";
import MaterialAudioTranscriber from "./editor/material-audio-transcriber";
import MaterialFindingEditor from "./finding-editor/MaterialFindingEditor";
import MaterialInterviewList from "./interview/material-interview-list";
import FindingEditorSkeletion from "./skeleton/FindingEditorSkeletion";
import { InterviewListDrawer } from "./interview-list-drawer";
import InsightDetails from "./insights/InsightDetails";
import NoteEditorSkeletion from "./skeleton/NoteEditorSkeletion";
import DiscoveryTopNav from "./discovery-top-nav";
import InsightDetailsSkeletion from "./skeleton/InsightDetailsSkeletion";
import DiscoveryTopToolbarSkeletion from "./skeleton/DiscoveryTopToolbarSkeletion";
import TranscriptSkeletion from "./skeleton/TranscriptSkeletion";

const getSectionStyles = (sectionKey,visibleCount) => {
  let baseWidth;
  let maxWidth;

  if (visibleCount === 1) {
    baseWidth = "100%";
    maxWidth  = "760px";
  } else if (visibleCount === 2) {
    baseWidth = "50%";
    maxWidth  = "none";
  } else {
    if (sectionKey === INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT) {
      baseWidth = "34%";
    } else {
      baseWidth = "33%";
    }
    maxWidth = "none";
  }
  return {
    width:baseWidth,
    maxWidth: maxWidth,
  };
};

export default function MaterialDiscovery(props: any) {
  const { findingLinksData, canvasDataAll } = props;

  const [base, setBase] = useAtom(baseAtom);
  const setFindings = useSetAtom(findingsAtom);
  const setInterviewFindingsAtom = useSetAtom(interviewFindingsAtom);
  const [isSavingFindings, setIsSavingFindings] = useState(false);
  const [searchInputValue, setSeachInputValue] = React.useState("");
  const [isPublishing, setIsPublishing] = useState(false);
  const { isInterviewListVisibility } = useInterviewListVisibility();
  const [reopenMenu, setReopenMenu] = useState<boolean>(true);
  const { keycloak } = useKeycloak();
  // const artifact = useAtomValue(artifactAtom);
  const [interview, setInterview] = useState(undefined);
  const theme = useTheme();
  const [openInterviewListDrawer, setOpenInterviewListDrawer] = React.useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedTab, setSelectedTab] = useState([
    INSIGHT_TABLE_SWITCH_VALUES.ANALYSIS,
    INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT,
    INSIGHT_TABLE_SWITCH_VALUES.INSIGHT
  ]);


  const client = useQueryClient();
  const allInterview = client.getQueryData([
    "interviews",
    { groupID: base.team.groupID },
  ]) as any[];

  const { data: customFindingTypesData } = useGetCustomFindingTypes(
    { accessToken: keycloak.token },
    { groupID: base.team.groupID },
    { enabled: !!base?.team?.groupID }
  );

  const {
    findings: findingsData,
    isFindingLoading,
    isSuccess: isSuccessFindings,
    isFetching
  } = useGetFindings(
    { accessToken: keycloak.token },
    { groupID: base.team.groupID, userID: base.user.userID, isMetaInsightEnabled: true },
    { enabled: !!(base?.team?.groupID && base?.user?.userID) }
  );

  const { mutateAsync: handleUpdateInterview } = useUpdateInterview(
    { accessToken: keycloak?.token },
    {
      onSuccess() {
        client.invalidateQueries({ queryKey: ["interviews"] });
      }
    }
  );

  React.useEffect(() => {
    if (findingsData && !isFindingLoading) {
      setFindings(findingsData?.findings);
      setInterviewFindingsAtom(findingsData?.interviews);
    }
  }, [findingsData]);

  let selectedArtifactType = base.selectedArtifactType
    ? base.selectedArtifactType
    : ["transcription"];

  if (
    !(
      selectedArtifactType.includes("transcription") ||
      selectedArtifactType.includes("notes")
    ) 
  ) {
    selectedArtifactType = ["notes"];
  }

  React.useEffect(() => {
    if (allInterview) {
      setInterview(getSelectedInterviewWithDefaultFallback(base, allInterview));
    }
  }, [allInterview, base.selectedInterview]);

  // const interviewID = interview ? interview?.interviewID : undefined;
  // console.log({interviewID,base : base?.selectedInterview})

  const filteredData = () => {
    let buffer = [];

    for (let i = 0; i < allInterview?.length; i++) {
      let ii = allInterview[i];

      if (searchInputValue !== "") {
        if (
          ii?.status === getViewMode(base, allInterview) &&
          ii?.interviewee.toLowerCase().includes(searchInputValue.toLowerCase())
        ) {
          buffer.push(ii);
        }
      } else {
        if (ii?.status === getViewMode(base, allInterview)) {
          buffer.push(ii);
        }
      }
    }

    return buffer;
  };

  const publishInterview = () => {
    setIsPublishing(true);

    handleUpdateInterview({
      ...interview,
      status: INTERVIEW_NAVIGATION_TABS.PUBLISHED
    })
      .then(() => {
        setIsPublishing(false);
        setDiscoveryViewMode(
          setBase,
          INTERVIEW_NAVIGATION_TABS.PUBLISHED,
          false,
          allInterview
        );
      })
      .catch(() => {})
      .finally(() => {});
  };

  React.useEffect(() => {
    if (isInterviewListVisibility && !base.discoveryFocusMode) {
      setReopenMenu(true);
    } else if (isInterviewListVisibility && base.discoveryFocusMode) {
      setReopenMenu(false);
      setOpenInterviewListDrawer(false);
    }

    const currentWidth = window.innerWidth;
    if (
      currentWidth >= INTERVIEW_PAGE_WIDTH_LIMITS.INTERVIEW_LIST &&
      reopenMenu
    ) {
      setDiscoveryFocusMode(setBase, false);
    }
  }, [isInterviewListVisibility, base.discoveryFocusMode]);
  React.useEffect(() => {
    const value = base?.selectedFinding;

    if (value) {
     try{
      const data = JSON?.parse(value?.quote);

      const result = data?.source === "transcript" ? "transcription" : data?.source ? "notes" : "metaanalysis";

      setSelectedArtifactType(setBase, result);
      setInsightSelection(setBase, undefined);
    }
    catch(err){}
    }
  }, []);

  const getArtifactID = () => {
    return base.selectedInterview;
  };

  const getFindingTypes = () => {
    return [];
  };

  const getAllEntries = () => {
    return [];
  };
 

  let layoutSections = [
    <DiscoveryNavigation
      prevProps={props}
      openInterviewListDrawer={openInterviewListDrawer}
      setOpenInterviewListDrawer={setOpenInterviewListDrawer}
      publishInterview={publishInterview}
      isPublishing={isPublishing}
      allInterview={allInterview} 
      setAnchorEl={setAnchorEl}
      interview={interview}
      setReopenMenu={setReopenMenu}
      isInterviewListVisibility={isInterviewListVisibility}
    />,
    interview === undefined ? (
     <TranscriptSkeletion/>
    ) : (
      <Box sx={{ height: "100%" }}>
       <Stack
          id="joy-discovery-text-editor"
          sx={{ height: "100%", width: "100%" }}
        >
        <Box sx={{ height: "100%" }}>
          <MaterialAudioTranscriber
            customFindingTypes={customFindingTypesData}
            // assemblies={assemblies}
            transcriptMode={base.interviewEditMode ? "edit" : "playback"}
            // interviewID={base?.selectedInterview}
          />
          </Box>
        </Stack>
      </Box>
    ),
    <Box sx={{ height: "100%" }}>
      <Stack direction="column" spacing="0" sx={{ height: "100%" }}>
        <MaterialFindingEditor
          activeArtifactName={selectedArtifactType}
          user={base.user}
          team={base.team}
          appState={base.app}
          cohort={base.cohort}
          canvasCategories={canvasDataAll?.categories}
          canvasEntries={getAllEntries()}
          canvas={canvasDataAll}
          findingTypes={getFindingTypes()}
          finding={undefined}
          clearSelectedFinding={() =>
            setBase((prev) => ({
              ...prev,
              selectedFinding: undefined,
            }))
          }
          quote={base.insightSelection}
          interviewID={base?.selectedInterview}
          artifactID={getArtifactID()}
          isSaving={isSavingFindings}
          create={() => {
            setIsSavingFindings(true);
          }}
          update={() => {
            setIsSavingFindings(true);
          }}
          findingLinksData={findingLinksData?.findingLinks}
        />
        <InsightDetails
           interviewID={base?.selectedInterview}
           selectedArtifactType={selectedArtifactType}
           findingLinksData={findingLinksData?.findingLinks}
           isFindingLoading={isFindingLoading}
           isSuccessFindings={isSuccessFindings}
           isFetching={isFetching}
        />
      </Stack>
    </Box>,
  ];
  const tabConfig = [
    {
      key: INSIGHT_TABLE_SWITCH_VALUES.ANALYSIS,
      hasTab: selectedTab?.includes(INSIGHT_TABLE_SWITCH_VALUES.ANALYSIS),
      style: {
        height: "100%",
        overflow: "hidden",
        margin: "0px 6px 0px 0px",
      },
      content: allInterview ? layoutSections[0] : <NoteEditorSkeletion />,
    },
    {
      key: INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT,
      hasTab: selectedTab?.includes(INSIGHT_TABLE_SWITCH_VALUES.TRANSCRIPT),
      style: {
        margin: "0px 6px",
      },
      content: allInterview ? layoutSections[1] : <TranscriptSkeletion />,
    },
    {
      key: INSIGHT_TABLE_SWITCH_VALUES.INSIGHT,
      hasTab: selectedTab?.includes(INSIGHT_TABLE_SWITCH_VALUES.INSIGHT),
      style: {
        margin: "0px 0px 0px 6px",
      },
      content: allInterview ? (
        layoutSections[2]
      ) : (
        <Stack direction="column" gap={2} sx={{ height: "100%" }}>
          <FindingEditorSkeletion />
          <InsightDetailsSkeletion />
        </Stack>
      ),
    },
  ];
  const visibleCount = tabConfig.filter((t) => t.hasTab).length;
  const artifactGlobalPanelHeight = "calc(100vh - 71px)";
return (
    <>
      <InterviewListDrawer
        anchorEl={anchorEl}
        open={openInterviewListDrawer}
        setIsOpen={setOpenInterviewListDrawer}
        interviews={filteredData()}
        interviewSearch={
          <CustomSearch
            placeholder="Search Interviewee Name..."
            inputValue={searchInputValue}
            updateInputValue={(newVal) => setSeachInputValue(newVal)}
          />
        }
        interviewList={
          <MaterialInterviewList
            interviews={filteredData()}
            setOpenInterviewListDrawer={setOpenInterviewListDrawer}
            openInterviewListDrawer={openInterviewListDrawer}
          />
        }
        setReopenMenu={setReopenMenu}
      />

      <Box
        sx={{
          maxHeight: artifactGlobalPanelHeight,
          minHeight: artifactGlobalPanelHeight,
          height: artifactGlobalPanelHeight,
          backgroundColor: theme.custom.mainBackgroundColor,
        }}
      >
        <Joyride
          steps={discoveryUserguides.test as any}
          continuous={true}
          run={true}
          debug={false}
          disableScrolling={true}
          spotlightClicks={true}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {allInterview ? (
            <DiscoveryTopNav
              publishInterview={publishInterview}
              isPublishing={isPublishing}
              prevProps={props}
              openInterviewListDrawer={openInterviewListDrawer}
              setOpenInterviewListDrawer={setOpenInterviewListDrawer}
              allInterview={allInterview}
              setAnchorEl={setAnchorEl}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
            />
          ) : (
            <DiscoveryTopToolbarSkeletion />
          )}
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignContent: "stretch",
              justifyContent: selectedTab.length === 1 ? "center" : "",
              flex: 1,
              minHeight: 0,
              px: "12px",
              pb: "12px",
            }}
          >
            {tabConfig.map(({ key, hasTab,  style, content }) =>
              hasTab && (
                <div
                  key={key}
                  style={{
                    ...style,
                    ...getSectionStyles(key, visibleCount),
                  }}
                >
                  {content}
                </div>
              )
            )}
          </Box>
        </div>
      </Box>
    </>
  );
}
